import { useMemo, useState } from 'react';
import useFetch from 'react-fetch-hook';
import { FaArrowDown, FaArrowUp } from 'react-icons/fa';
import { Spain } from './maps/Spain';
import { Table } from './Table';

/** Information about a region of a country. */
export type Region = {
  code: string;
  name: string;
  color: string;
};

/** Structure of data sent into the table. */
export type TableData = {
  color: string;
  region: string;
  price: number;
  previousPrices: number[];
  change: number;
  timestamp: string;
};

/** Map regions from code to name and color. */
const regions: Region[] = [
  { code: 'península', name: 'Península', color: 'blue' },
  { code: 'canarias', name: 'Canarias', color: 'green' },
  { code: 'baleares', name: 'Baleares', color: 'purple' },
  { code: 'ceuta', name: 'Ceuta', color: 'yellow' },
  { code: 'melilla', name: 'Melilla', color: 'red' },
];

/** App to display an overview of spot prices in the electricity market. */
export const App = () => {
  const [latestTimestamp, setLatestTimestamp] = useState<string | null>(null)
  const { isLoading, data } = useFetch<any>(
    'https://ovuvdmhqcg.execute-api.eu-central-1.amazonaws.com/production/spotprices?country=ES'
  );
  
  const tableData = useMemo<TableData[]>(() => {
    console.log(data)
    const currentDate = new Date()
    return data
      ? regions.map(({ code, color, name }) => {
          const regionData = data.data[code].filter((item: any, i: number) => {
            const itemDate = new Date(item.datetime)
            return currentDate.getTime() > itemDate.getTime()
          });
          setLatestTimestamp(regionData[regionData.length - 1].datetime_utc)

          return {
            color,
            region: name,
            price: regionData[regionData.length - 1].value,
            previousPrices: [1],
            change: (regionData[regionData.length - 1].value - regionData[0].value) / regionData[0].value,
            timestamp: regionData.datetime
          };
        })
      : [];
  }, [data]);

  const lastUpdated = useMemo(
    () =>
      latestTimestamp
        ? new Date(latestTimestamp).toLocaleString().slice(0, -3)
        : null,
    [latestTimestamp]
  );

  const [activeRegion, setActiveRegion] = useState<string | null>(null);

  return isLoading ? (
    <span className="text-base">Cargando...</span>
  ) : (
    <div className="md:flex md:justify-center md:items-center md:gap-12">
      <div className="hidden md:block md:w-full xl:w-9/12">
        <Spain onHover={setActiveRegion} onBlur={() => setActiveRegion(null)} activeRegion={activeRegion} />
      </div>
      <div>
        <Table
          activeRow={(row) => !activeRegion || row.region === activeRegion}
          columns={['color', 'region', 'price', 'change']}
          columnLabels={['', 'Área geográfica', 'Precio del kWh', 'Evolución 24 h']}
          columnClasses={['hidden md:flex']}
          data={tableData}
          onHover={({ region }) => setActiveRegion(region)}
          onBlur={() => setActiveRegion(null)}
        >
          {(column, { color, region, price, change }) => {
            switch (column) {
              case 'color':
                return <div className={`w-6 h-6 bg-${color}-500 rounded-full`}></div>;
              case 'region':
                return <div className="text-left">{region}</div>;
              case 'price':
                return (
                  <div className="flex gap-2">
                    <span className="font-semibold text-sm md:text-base">
                      {new Intl.NumberFormat('es', { minimumFractionDigits: 3, maximumFractionDigits: 3 }).format(
                        price
                      )}
                    </span>
                    <span className="font-normal text-gray-500 text-sm md:text-base">€/kWh</span>
                  </div>
                );
              case 'change':
                return (
                  <div className="flex items-center gap-2">
                    {change > 0 ? (
                      <FaArrowUp className="fill-current text-red-500" />
                    ) : (
                      <FaArrowDown className="fill-current text-green-500" />
                    )}
                    <span className="font-semibold text-sm md:text-base">
                      {new Intl.NumberFormat('es', { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(
                        change * 100
                      )}{' '}
                    </span>
                    <span className="font-normal text-gray-500 text-sm md:text-base"> %</span>
                  </div>
                );
            }
          }}
        </Table>
        <div className="pt-4 text-xs text-gray-400">
          Los precios corresponden a la tarifa PVPC 2.0TD y no incluyen impuestos. Datos obtenidos de{' '}
          <a href="https://www.esios.ree.es/es/pvpc" target="_blank" rel="noreferrer">
            REE
          </a>
          .
          <br />
          Los datos se actualizan cada hora. {lastUpdated && `Última actualización: ${lastUpdated}.`}
        </div>
      </div>
    </div>
  );
};
